import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Smartphone, Apple, Globe, Zap, CheckCircle, ArrowRight, Shield, Code2, Users } from 'lucide-react';

const PLATFORMS = [
  { icon: Apple,      title: 'iOS App Development',          desc: 'Native Swift apps built for iPhone & iPad with App Store submission support.', tags: ['Swift / SwiftUI', 'Core Data', 'Push Notifications', 'App Store'] },
  { icon: Smartphone, title: 'Android App Development',      desc: 'High-performance native Android apps using Kotlin for all device types.', tags: ['Kotlin / Jetpack', 'Material Design', 'Google Play', 'Offline Mode'] },
  { icon: Globe,      title: 'Cross-Platform (Flutter)',     desc: 'One codebase, two platforms — Flutter apps that feel native on both iOS & Android.', tags: ['Flutter / Dart', 'Single Codebase', 'Fast Delivery', 'Cost Effective'] },
  { icon: Code2,      title: 'React Native Apps',           desc: "JavaScript-based cross-platform apps backed by React's component ecosystem.", tags: ['React Native', 'Expo', 'REST APIs', 'Live Updates'] },
];

const FEATURES = [
  'UI/UX design & interactive prototyping',
  'Backend API development & integration',
  'Payment gateway integration (Razorpay, Stripe)',
  'Push notifications & real-time messaging',
  'Offline mode & local data caching',
  'Analytics & crash reporting setup',
  'App Store & Play Store submission',
  '30-day post-launch bug-fix warranty',
];

const PROCESS = [
  { n: '01', t: 'Discovery', d: 'Requirements, wireframes & tech stack selection' },
  { n: '02', t: 'Design',    d: 'UI/UX design, prototyping & client approval' },
  { n: '03', t: 'Build',     d: 'Agile sprints, API integration & QA testing' },
  { n: '04', t: 'Launch',    d: 'Store submission, monitoring & hypercare' },
];

const fade = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

export default function MobileAppDevelopment() {
  return (
    <>
      <Helmet>
        <title>Mobile App Development Company India — iOS & Android | Vibhraad Technology</title>
        <meta name="description" content="Professional mobile app development services in India. Native iOS, Android, Flutter & React Native apps. 200+ apps delivered. Get a free quote from Vibhraad Technology." />
        <meta name="keywords" content="mobile app development company india, ios app development, android app development, flutter app development, react native development, cross platform apps" />
        <link rel="canonical" href="https://vibhraad.com/mobile-app-development" />
      </Helmet>

      <div className="bg-white min-h-screen">

        {/* Hero */}
        <section className="bg-gray-950 pt-20 pb-16 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle,#6b7280 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
          <div className="absolute -top-20 left-1/3 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-5xl mx-auto px-5 text-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0, transition: { duration: 0.6 } }}>
              <div className="inline-flex items-center gap-2 bg-blue-600/15 border border-blue-500/25 text-blue-400 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
                <Smartphone className="w-3.5 h-3.5" /> Mobile App Development
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
                Build Apps That Users<br />
                <span className="text-blue-400">Love & Keep Coming Back To</span>
              </h1>
              <p className="text-gray-400 text-base max-w-2xl mx-auto mb-8 leading-relaxed">
                From iOS to Android, Flutter to React Native — we design and build mobile apps that are fast, intuitive, and built to scale. Starting from ₹1,50,000.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold px-7 py-3.5 rounded-xl transition-colors shadow-lg shadow-blue-600/25">
                  Get Free Quote <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/pricing" className="inline-flex items-center justify-center gap-2 bg-white/8 border border-white/15 text-white font-semibold px-7 py-3.5 rounded-xl hover:bg-white/12 transition-colors">
                  View Pricing
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-blue-600 py-10">
          <div className="max-w-4xl mx-auto px-5 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[['200+','Apps Delivered'],['4.8★','Avg App Rating'],['10-16 wks','Avg Timeline'],['30 days','Free Support']].map(([v,l]) => (
              <div key={l}><p className="text-2xl font-black text-white mb-1">{v}</p><p className="text-blue-100 text-xs font-semibold uppercase tracking-wide">{l}</p></div>
            ))}
          </div>
        </section>

        {/* Platforms */}
        <section className="py-20 max-w-6xl mx-auto px-5">
          <div className="text-center mb-12">
            <p className="text-blue-600 text-xs font-bold uppercase tracking-widest mb-2">Platforms & Technologies</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">Every Platform. One Partner.</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {PLATFORMS.map(({ icon: Icon, title, desc, tags }) => (
              <motion.div key={title} initial="hidden" whileInView="show" variants={fade} viewport={{ once: true }}
                className="p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-4 shadow-md shadow-blue-600/20">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-black text-gray-900 text-lg mb-2">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{desc}</p>
                <div className="flex flex-wrap gap-2">
                  {tags.map(t => <span key={t} className="text-xs bg-blue-50 text-blue-700 font-semibold px-3 py-1 rounded-full border border-blue-100">{t}</span>)}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="bg-gray-950 py-20">
          <div className="max-w-5xl mx-auto px-5">
            <div className="text-center mb-12">
              <p className="text-blue-400 text-xs font-bold uppercase tracking-widest mb-2">What's Included</p>
              <h2 className="text-3xl md:text-4xl font-black text-white">Everything You Need in One Package</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {FEATURES.map(f => (
                <div key={f} className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/8">
                  <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-sm">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 max-w-5xl mx-auto px-5">
          <div className="text-center mb-12">
            <p className="text-blue-600 text-xs font-bold uppercase tracking-widest mb-2">How We Work</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">From Idea to App Store in 4 Stages</h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {PROCESS.map(({ n, t, d }) => (
              <motion.div key={n} initial="hidden" whileInView="show" variants={fade} viewport={{ once: true }} className="text-center">
                <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-600/25">
                  <span className="text-white font-black text-lg">{n}</span>
                </div>
                <h3 className="font-black text-gray-900 mb-1">{t}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gray-950 py-16 text-center">
          <div className="max-w-2xl mx-auto px-5">
            <h2 className="text-3xl font-black text-white mb-4">Start Building Your App Today</h2>
            <p className="text-gray-400 text-sm mb-8">Free consultation · Fixed-price quotes · First milestone in 2 weeks</p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold px-8 py-4 rounded-xl transition-colors shadow-xl shadow-blue-600/25">
              Get Free Consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

      </div>
    </>
  );
}
